/**
 * Lazy loader for the tour viewer (~26 KB gzip — the guides engine plus
 * @floating-ui/dom).
 *
 * Tours are only needed on pages that actually host one, which is a small
 * minority of the pages the chat widget runs on. Statically importing
 * `bootTour` put the whole engine in app.js, so every chat visitor paid
 * for it whether or not a tour existed on the page — app.js measured
 * 104 KB gzip against main's 78 KB. It now ships as a SEPARATE ESM chunk
 * (`tours.js`, built by esbuild.config.mjs next to app.js) and loads via
 * native dynamic `import()` on first use.
 *
 * Same mechanism as the Daily split (see voice/daily-loader.ts): the URL
 * is computed at runtime from the widget script's own directory so esbuild
 * cannot statically resolve it and leaves the `import()` native, which
 * classic scripts are allowed to call in every browser we support.
 */

/** Boot context the main bundle passes down (see tours/telemetry.ts). */
export type TourBootContext = {
  visitorId?: string;
  playerSource?: 'preview' | 'lightbox' | 'embed' | 'share' | 'chat';
  /** Bot-audible edge bridge — ducks tour-video source audio while the
      agent speaks (callback, never a cross-bundle signal). */
  subscribeBotAudible?: (cb: (talking: boolean) => void) => () => void;
};

export type BootTour = (mount: HTMLElement, ctx?: TourBootContext) => void;
type ToursModule = { bootTour?: BootTour; bootProject?: BootTour };

let chunkBase: string | null = null;
let cached: Promise<ToursModule> | null = null;

/** Set once at boot from config.assetBaseUrl (the app.js directory). */
export function setToursChunkBase(base: string): void {
  chunkBase = base.replace(/\/$/, '');
}

function loadModule(): Promise<ToursModule> {
  if (!cached) {
    if (!chunkBase) {
      return Promise.reject(
        new Error('[showpilot] tours chunk base not configured before tour boot'),
      );
    }
    const url = `${chunkBase}/tours.js`;
    cached = import(/* @vite-ignore */ url)
      .then((m: ToursModule) => {
        if (typeof m.bootTour !== 'function') {
          throw new Error('[showpilot] tours chunk loaded but bootTour is missing');
        }
        return m;
      })
      .catch((err: unknown) => {
        // Reset so a later attempt can retry — a transient network failure
        // must not permanently kill tours for the session.
        cached = null;
        throw err;
      });
  }
  return cached;
}

export function loadTours(): Promise<BootTour> {
  return loadModule().then((m) => m.bootTour!);
}

let cachedProject: Promise<BootTour> | null = null;

/** The project player — its OWN chunk (`project.js`): the tour player
    plus the checklist panel + tour switching, loaded only where a project
    mount exists so tour pages never pay for it. */
export function loadProject(): Promise<BootTour> {
  if (!cachedProject) {
    if (!chunkBase) {
      return Promise.reject(
        new Error('[showpilot] tours chunk base not configured before project boot'),
      );
    }
    const url = `${chunkBase}/project.js`;
    cachedProject = import(/* @vite-ignore */ url)
      .then((m: ToursModule) => {
        if (typeof m.bootProject !== 'function') {
          throw new Error('[showpilot] project chunk loaded but bootProject is missing');
        }
        return m.bootProject;
      })
      .catch((err: unknown) => {
        cachedProject = null;
        throw err;
      });
  }
  return cachedProject;
}

/** Test hook — reset module state between cases. */
export function resetToursLoaderForTests(): void {
  chunkBase = null;
  cached = null;
  cachedProject = null;
}
